import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────────────────
   Navbar.js — shared top nav for all Flap pages
   ─────────────────────────────────────────────────────────────────────────────
   Visibility rules:
   • HIDDEN on pages with their own SIDEBAR nav (Profile, Admin, Contacts,
     Write-NFC, Analytics, Business) — those pages self-contain navigation.
   • SHOWN on all other pages: Home, Messages, Dashboard, public pages, etc.

   Theme: #1c73ba brand blue, white surface, Inter font.
   Consistent with Profile.js / UserInfo.jsx / theme.css.
───────────────────────────────────────────────────────────────────────────── */

// Pages with their own full sidebar — Navbar hides itself here
const SIDEBAR_PAGES = [
  '/profile', '/analytics', '/contacts', '/write-nfc',
  '/admin', '/business', '/ai-agent', '/products',
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@700&display=swap');

.nb {
  position: sticky;
  top: 0;
  z-index: 200;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  font-family: 'Inter', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  transition: box-shadow .2s;
}
.nb.scrolled { box-shadow: 0 1px 12px rgba(0,0,0,0.07); }

.nb-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Logo */
.nb-logo {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1c73ba;
  text-decoration: none;
  letter-spacing: -.5px;
  flex-shrink: 0;
  margin-right: 8px;
}
.nb-logo span { color: #f59e0b; }

/* Nav links section — expands to fill space */
.nb-links {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
}

.nb-link {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  transition: all .15s;
  white-space: nowrap;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.nb-link:hover { background: #f3f4f6; color: #111827; }
.nb-link.active { color: #1c73ba; background: #e8f3fb; font-weight: 600; }

/* CTA buttons */
.nb-ctas {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.nb-btn {
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: all .15s;
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 36px;
}
.nb-btn-ghost {
  background: transparent;
  color: #1c73ba;
  border: 1.5px solid #1c73ba;
}
.nb-btn-ghost:hover { background: #e8f3fb; }
.nb-btn-solid {
  background: #1c73ba;
  color: #fff;
  border: 1.5px solid #1c73ba;
}
.nb-btn-solid:hover { background: #155fa0; border-color: #155fa0; }

/* Mobile hamburger */
.nb-ham {
  display: none;
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
  margin-left: 4px;
}
.nb-ham:hover { border-color: #1c73ba; color: #1c73ba; background: #e8f3fb; }

/* Drawer */
.nb-drawer {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 9999;
}
.nb-drawer.open { display: block; }

.nb-drawer-bg {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(4px);
}

.nb-drawer-panel {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 272px;
  background: #fff;
  padding: 20px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
  box-shadow: -4px 0 24px rgba(0,0,0,0.1);
}

.nb-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.nb-drawer-logo {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1c73ba;
  text-decoration: none;
}
.nb-drawer-logo span { color: #f59e0b; }
.nb-drawer-close {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 15px;
}
.nb-drawer-close:hover { background: #f3f4f6; color: #111; }

.nb-drawer-section {
  font-size: 9px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 10px 10px 4px;
}

.nb-drawer-link {
  padding: 10px 12px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: all .15s;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  width: 100%;
}
.nb-drawer-link:hover { background: #f3f4f6; color: #111827; }
.nb-drawer-link.active { color: #1c73ba; background: #e8f3fb; font-weight: 600; }
.nb-drawer-link.danger { color: #ef4444; }
.nb-drawer-link.danger:hover { background: #fef2f2; }
.nb-drawer-link.primary { color: #1c73ba; font-weight: 700; }
.nb-drawer-link.primary:hover { background: #e8f3fb; }

.nb-div { height: 1px; background: #e5e7eb; margin: 8px 4px; }

/* User chip in drawer */
.nb-user-chip {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  background: #e8f3fb;
  border-radius: 10px;
  margin-bottom: 8px;
  border: 1px solid rgba(28,115,186,0.15);
}
.nb-user-av {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1c73ba, #5aaef7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.nb-user-name { font-size: 13px; font-weight: 600; color: #111827; }
.nb-user-role { font-size: 10px; color: #1c73ba; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }

@media (max-width: 700px) {
  .nb-links { display: none; }
  .nb-ctas { display: none; }
  .nb-ham { display: flex; }
  .nb-drawer.open { display: block; }
}
@media (min-width: 701px) {
  .nb-drawer { display: none !important; }
}
`;

export default function Navbar() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const [open,     setOpen]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const token  = localStorage.getItem('token');
  const isAuth = !!token;

  // Decode JWT for role + username
  let role = 'user', username = '';
  try {
    const p = JSON.parse(atob(token.split('.')[1]));
    role     = p.role     || 'user';
    username = p.username || p.email?.split('@')[0] || '';
  } catch {}

  const isAdmin = ['admin', 'superadmin'].includes(role);

  // Hide on pages with their own sidebar nav
  const hasSidebar = SIDEBAR_PAGES.some(p => location.pathname.startsWith(p));
  if (hasSidebar) return null;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
    setOpen(false);
  };

  const isActive = path => location.pathname === path;

  // ── Nav link sets ──────────────────────────────────────────────────────────
  const publicLinks = [
    { label: 'Home',     path: '/'             },
    { label: 'Products', path: '/physical-card' },
    { label: 'Pricing',  path: '/pricing'       },
    { label: 'About',    path: '/about'         },
  ];

  const authLinks = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Profile',   path: '/profile'   },
    { label: 'Messages',  path: '/messages'  },
    ...(isAdmin ? [{ label: 'Admin', path: '/admin' }] : []),
  ];

  const links = isAuth ? authLinks : publicLinks;

  return (
    <>
      <style>{CSS}</style>
      <nav className={`nb${scrolled ? ' scrolled' : ''}`}>
        <div className="nb-inner">
          {/* Logo */}
          <Link to="/" className="nb-logo">flap<span>.</span></Link>

          {/* Desktop nav links */}
          <div className="nb-links">
            {links.map(l => (
              <Link key={l.path} to={l.path}
                className={`nb-link ${isActive(l.path) ? 'active' : ''}`}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="nb-ctas">
            {isAuth ? (
              <button className="nb-btn nb-btn-ghost" onClick={handleLogout}>
                Log out
              </button>
            ) : (
              <>
                <Link to="/login"    className="nb-btn nb-btn-ghost">Log in</Link>
                <Link to="/register" className="nb-btn nb-btn-solid">Get started →</Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="nb-ham" onClick={() => setOpen(true)} aria-label="Open menu">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6"  x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div className={`nb-drawer ${open ? 'open' : ''}`} role="dialog" aria-modal="true">
        <div className="nb-drawer-bg" onClick={() => setOpen(false)}/>
        <div className="nb-drawer-panel">

          {/* Drawer header */}
          <div className="nb-drawer-header">
            <Link to="/" className="nb-drawer-logo">
              flap<span>.</span>
            </Link>
            <button className="nb-drawer-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          {/* Logged-in user chip */}
          {isAuth && username && (
            <div className="nb-user-chip">
              <div className="nb-user-av">
                {username[0].toUpperCase()}
              </div>
              <div>
                <div className="nb-user-name">{username}</div>
                <div className="nb-user-role">{role}</div>
              </div>
            </div>
          )}

          {/* Nav links */}
          <div className="nb-drawer-section">Navigation</div>
          {links.map(l => (
            <Link key={l.path} to={l.path}
              className={`nb-drawer-link ${isActive(l.path) ? 'active' : ''}`}>
              {l.label}
            </Link>
          ))}

          <div className="nb-div"/>

          {/* Auth actions */}
          {isAuth ? (
            <>
              <Link to="/profile" className={`nb-drawer-link ${isActive('/profile') ? 'active' : ''}`}>
                My Profile
              </Link>
              <button className="nb-drawer-link danger" onClick={handleLogout}>
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login"    className={`nb-drawer-link ${isActive('/login') ? 'active' : ''}`}>Log in</Link>
              <Link to="/register" className="nb-drawer-link primary">Get started →</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}